/**
 * Edge slide command — per-frame preview.
 *
 * Edge slide is unusual enough to warrant its own module:
 *   - It has no numeric-input confirm path (numeric slide factor isn't
 *     meaningful outside a pointer drag).
 *   - The rails are computed once at `enterCommand` time (see
 *     commandEnter.ts) and consumed here each frame.
 *   - The snap behavior is completely different from translate/scale/rotate:
 *     it projects the snap target onto a reference rail and derives a slide
 *     factor, not a world-space offset.
 *
 * Each selected vertex is moved along its associated rail by a shared
 * slide factor `dt`:
 *   dt = 0      → original position
 *   dt < 0      → slides toward A (start neighbor)
 *   dt > 0      → slides toward B (end neighbor; falls back to A when the
 *                 vertex has only one non-selected neighbor)
 */

import * as THREE from "three/webgpu";
import { querySnap, setLastSnapResult } from "../../modeling/snap.ts";
import type { EdgeSlideRail } from "../storeTypes.ts";
import type { ApplyLiveContext } from "./core.ts";
import { _scratchMat4B, _scratchVec3C, _scratchVec3D } from "./core.ts";

export function applyLiveEdgeSlide(
  ctx: ApplyLiveContext,
  newVerts: Float32Array,
  objMatrix: THREE.Matrix4,
): void {
  const { state, cb, camera, rect, screenX, screenY, dx, dy } = ctx;

  const rails = cb.edgeSlideRails!;

  // Compute screen-space rail direction from an average of A→B (or
  // origin→A for single-neighbor) to project mouse delta into a slide
  // factor.
  let avgDirX = 0,
    avgDirY = 0;
  let railCount = 0;
  for (const [, rail] of rails) {
    const from = rail.b ? rail.a : rail.origin;
    const to = rail.b ?? rail.a;
    const fromSS = _scratchVec3C
      .set(from[0], from[1], from[2])
      .applyMatrix4(objMatrix)
      .project(camera);
    const toSS = _scratchVec3D
      .set(to[0], to[1], to[2])
      .applyMatrix4(objMatrix)
      .project(camera);
    avgDirX += (toSS.x - fromSS.x) * rect.width * 0.5;
    avgDirY += -(toSS.y - fromSS.y) * rect.height * 0.5;
    railCount++;
  }
  if (railCount > 0) {
    avgDirX /= railCount;
    avgDirY /= railCount;
  }
  const dirLenSq = avgDirX * avgDirX + avgDirY * avgDirY;
  let dt = dirLenSq > 1e-6 ? (dx * avgDirX + dy * avgDirY) / dirLenSq : 0;

  // Snap override: project snap target onto a reference rail to derive dt.
  // Picks the rail closest to the cursor (NDC) as the reference, then
  // projects the snap target onto its A and B segments to find the
  // closest-approach dt value.
  if (state.snapEnabled) {
    const ndcX = ((screenX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((screenY - rect.top) / rect.height) * 2 + 1;
    const snapResult = querySnap(
      ndcX,
      ndcY,
      state.snapTarget,
      new Set<string>(),
    );
    setLastSnapResult(snapResult);
    if (snapResult) {
      const invObj = _scratchMat4B.copy(objMatrix).invert();
      const snapLocalTarget = new THREE.Vector3(
        ...snapResult.worldPosition,
      ).applyMatrix4(invObj);

      const tx = snapLocalTarget.x,
        ty = snapLocalTarget.y,
        tz = snapLocalTarget.z;
      let bestRail: EdgeSlideRail | null = null;
      let bestDistSq = Infinity;
      for (const [, rail2] of rails) {
        const oSS = _scratchVec3D
          .set(rail2.origin[0], rail2.origin[1], rail2.origin[2])
          .applyMatrix4(objMatrix)
          .project(camera);
        const d2 = (oSS.x - ndcX) ** 2 + (oSS.y - ndcY) ** 2;
        if (d2 < bestDistSq) {
          bestDistSq = d2;
          bestRail = rail2;
        }
      }
      if (bestRail) {
        const ox2 = bestRail.origin[0],
          oy2 = bestRail.origin[1],
          oz2 = bestRail.origin[2];
        const dxA = bestRail.a[0] - ox2,
          dyA = bestRail.a[1] - oy2,
          dzA = bestRail.a[2] - oz2;
        const lenSqA = dxA * dxA + dyA * dyA + dzA * dzA;
        const tA =
          lenSqA > 1e-12
            ? Math.max(
                0,
                Math.min(
                  1,
                  ((tx - ox2) * dxA + (ty - oy2) * dyA + (tz - oz2) * dzA) /
                    lenSqA,
                ),
              )
            : 0;

        if (bestRail.b) {
          const dxB = bestRail.b[0] - ox2,
            dyB = bestRail.b[1] - oy2,
            dzB = bestRail.b[2] - oz2;
          const lenSqB = dxB * dxB + dyB * dyB + dzB * dzB;
          const tB =
            lenSqB > 1e-12
              ? Math.max(
                  0,
                  Math.min(
                    1,
                    ((tx - ox2) * dxB + (ty - oy2) * dyB + (tz - oz2) * dzB) /
                      lenSqB,
                  ),
                )
              : 0;
          // Pick the segment whose projected point is closer to the snap target
          const pAx = ox2 + dxA * tA,
            pAy = oy2 + dyA * tA,
            pAz = oz2 + dzA * tA;
          const pBx = ox2 + dxB * tB,
            pBy = oy2 + dyB * tB,
            pBz = oz2 + dzB * tB;
          const distSqA = (tx - pAx) ** 2 + (ty - pAy) ** 2 + (tz - pAz) ** 2;
          const distSqB = (tx - pBx) ** 2 + (ty - pBy) ** 2 + (tz - pBz) ** 2;
          dt = distSqA <= distSqB ? -tA : tB;
        } else {
          dt = tA;
        }
      }
    }
  } else {
    setLastSnapResult(null);
  }

  for (const [vi, rail] of rails) {
    let px2: number, py2: number, pz2: number;
    if (rail.b == null) {
      // Single neighbor: slide from origin toward A, t in [0, 1]
      const t = Math.max(0, Math.min(1, dt));
      px2 = rail.origin[0] + (rail.a[0] - rail.origin[0]) * t;
      py2 = rail.origin[1] + (rail.a[1] - rail.origin[1]) * t;
      pz2 = rail.origin[2] + (rail.a[2] - rail.origin[2]) * t;
    } else {
      // Two neighbors: t < 0 slides toward A, t > 0 slides toward B
      const t = Math.max(-1, Math.min(1, dt));
      if (t <= 0) {
        const s = -t; // 0..1
        px2 = rail.origin[0] + (rail.a[0] - rail.origin[0]) * s;
        py2 = rail.origin[1] + (rail.a[1] - rail.origin[1]) * s;
        pz2 = rail.origin[2] + (rail.a[2] - rail.origin[2]) * s;
      } else {
        px2 = rail.origin[0] + (rail.b[0] - rail.origin[0]) * t;
        py2 = rail.origin[1] + (rail.b[1] - rail.origin[1]) * t;
        pz2 = rail.origin[2] + (rail.b[2] - rail.origin[2]) * t;
      }
    }
    newVerts[vi * 3] = px2;
    newVerts[vi * 3 + 1] = py2;
    newVerts[vi * 3 + 2] = pz2;
  }
}
